import { useEffect, useState } from 'react'
import { Autocomplete, Button, Typography } from '@equinor/eds-core-react'
import styled from 'styled-components'

import { baseFont, InputStyledLabel } from '@/styles/layout'
import { PlantDto } from '@/core/models/types'
import { addSnackBarMessage } from '@/core/store/globalStore'
import { useGetUserSettings } from '@/core/hooks-and-services/userSettingsService'
import { ALL_PLANTS_OPTION, normalizePlantSelection, useCurrentPlant } from '@/core/hooks-and-services/useCurrentPlant'

type Props = {
    onSaved?: () => void
}

function PlantForm({ onSaved }: Props) {
    const { data } = useGetUserSettings()
    const { currentPlant, setCurrentPlant, plants, isLoading, isHydrated } = useCurrentPlant()
    const [selectedPlant, setSelectedPlant] = useState<PlantDto | undefined>(ALL_PLANTS_OPTION)

    useEffect(() => {
        if (isHydrated) setSelectedPlant(currentPlant ?? ALL_PLANTS_OPTION)
    }, [isHydrated, currentPlant])

    const handlePlantChange = ({ selectedItems }: { selectedItems: unknown[] }) => {
        setSelectedPlant(selectedItems[0] as PlantDto | undefined)
    }

    const handleUpdate = async () => {
        const ok = await setCurrentPlant(normalizePlantSelection(selectedPlant))
        if (ok) {
            addSnackBarMessage(`User settings saved`)
            onSaved?.()
        }
    }

    return (
        <>
            {data?.isNew && (
                <StyledDescription>
                    To register any device to a specific plant, please select plant. Make sure you have correct
                    authentication at AccessIT. This user setting will be remembered.
                </StyledDescription>
            )}
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Select plant</InputStyledLabel>
                    </RowStyle>
                }
                options={[ALL_PLANTS_OPTION, ...plants]}
                optionLabel={(plant: unknown) => (plant as PlantDto).description ?? ''}
                selectedOptions={selectedPlant ? [selectedPlant] : []}
                onOptionsChange={handlePlantChange}
                autoWidth
            />
            <ButtonContainer>
                <Button disabled={isLoading} onClick={handleUpdate} style={{ width: '112px' }}>
                    Save
                </Button>
            </ButtonContainer>
        </>
    )
}

const StyledDescription = styled(Typography)`
    font-family: Equinor;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.2px;
    max-width: 248px;
`

const StyledAutocomplete = styled(Autocomplete)`
    ${baseFont}
    margin-top: 16px;
    margin-bottom: 16px;
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
    > * {
        margin-right: 0.3rem;
    }
`

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
`

export default PlantForm
